import { Body } from "../../../components/main-body/body";
import { ArticleModal } from "./component/article-modal";
import "./article-management.css";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import { useAdminResource } from "../components/hooks/useAdminResource";
import ApiArticles from "../../../api/articles";
import AdminResourceTable from "../components/layouts/general/admin-resource-table";
import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import useNotification from "../../../components/notification/notification";
import { isReferencedError, hasReferencedFailure } from "../../../common/utils/referenced-error";
import DeleteModal from "../components/layouts/common/delete-modal";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import { useCan } from "../../../common/utils/usePermissions";

const ALLOWED_STATUSES = new Set(["active", "pending", "inactive"]);

function parseStatusesFromSearch(search) {
  const sp = new URLSearchParams(search || "");
  const rawAll = sp.getAll("statuses").flatMap((v) => String(v).split(","));

  const statuses = rawAll
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v) => ALLOWED_STATUSES.has(v));

  return statuses.length ? statuses : null;
}

const arraysEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const ArticleManagement = ({ mode }) => {
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const {
    form,
    list,
    active,
    update,
    authors,
    open,
    close,
    createAndOpen,
    search,
    handleUpdateSearchValue,
    remove,
    tags,
    categories,
    updateFilterValue: updateFilterValueFromHook, // IMPORTANT: alias
    updateSorting,
    updateHasImage,
    updateIsUpdatedImage,
    isUpdatedImage,
    isLoading,
    getResources,
    updateResourceSilent,
    hasMore,
    fetchNext,
  } = useAdminResource(
    ApiArticles,
    "pages",
    "articles.notification",
    "articles",
    "page",
    "content"
  );

  const { id } = useParams();
  const location = useLocation();
  const { openNotification } = useNotification();
  const navigate = useProjectNavigate();
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  // init from URL (?statuses=pending)
  const [filters, setFilters] = useState(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);
    return statusesFromUrl ? { statuses: statusesFromUrl } : {};
  });

  const [isActionLoading, setActionLoading] = useState(false);

  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [isToggleOpen, setToggleOpen] = useState(false);
  const [toggleItem, setToggleItem] = useState(null);

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  const actionLockRef = useRef({
    publish: false,
    discard: false,
    duplicate: false,
    delete: false,
    toggle: false,
  });

  const runLockedAction = async (key, action) => {
    if (actionLockRef.current[key]) return;

    actionLockRef.current[key] = true;
    setActionLoading(true);

    try {
      await action();
    } finally {
      actionLockRef.current[key] = false;
      setActionLoading(false);
    }
  };

  const refreshActive = useCallback(
    async (id) => {
      if (!id) return;
      const resp = await ApiArticles.getById(id);
      const full = resp?.data?.page || resp?.data?.article || resp?.data || null;
      if (full) open(full, mode);
    },
    [open, mode]
  );

  const updateFilterValueControlled = useCallback(
    (value) => {
      const next = value || {};
      setFilters(next);
      updateFilterValueFromHook?.(next);
    },
    [updateFilterValueFromHook]
  );

  useEffect(() => {
    updateFilterValueFromHook?.(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);

    setFilters((prev) => {
      const prevStatuses = prev?.statuses || null;
      const nextStatuses = statusesFromUrl || null;

      const same =
        (prevStatuses === null && nextStatuses === null) ||
        (Array.isArray(prevStatuses) &&
          Array.isArray(nextStatuses) &&
          arraysEqual(prevStatuses, nextStatuses));

      if (same) return prev;

      const next = { ...(prev || {}) };
      if (nextStatuses) next.statuses = nextStatuses;
      else delete next.statuses;

      // sync to hook too
      updateFilterValueFromHook?.(next);

      return next;
    });
  }, [location.search, updateFilterValueFromHook]);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } =
    useBulkSelection(list, { rowKey: "id" });

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiArticles.removeBulk(selectedKeys);
      const {
        deletedIds = [],
        deletedGroupIds = [],
        deletedCount = 0,
        failed = [],
      } = resp?.data || {};

      const successCount = deletedCount || deletedGroupIds.length || deletedIds.length;
      const failedIds = failed.map((f) => f.id);

      if (successCount) {
        const plural = successCount > 1;
        openNotification?.({
          type: "success",
          message: (
            <p>
              {successCount} {plural ? "items were" : "item was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: hasReferencedFailure(failed) ? (
            <p>Unable to delete this object because it is referenced by another object in the system.</p>
          ) : (
            <p>
              Unable to delete {failedIds.length} {plural ? "items" : "item"}
            </p>
          ),
        });
      }

      if (!failedIds.length) clearSelection();
      else setSelectedKeys(failedIds);

      await getResources();
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete articles</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    getResources,
    clearSelection,
    setSelectedKeys,
  ]);

  const openDeleteConfirm = (item) => {
    setDeleteItem(item);
    setDeleteOpen(true);
  };
  const closeDeleteConfirm = () => {
    setDeleteOpen(false);
    setDeleteItem(null);
  };

  const openToggleConfirm = (item) => {
    setToggleItem(item);
    setToggleOpen(true);
  };
  const closeToggleConfirm = () => {
    setToggleOpen(false);
    setToggleItem(null);
  };

  const openedIdRef = useRef(null);

  const confirmDelete = async () => {
    if (!deleteItem) return;

    await runLockedAction("delete", async () => {
      await remove(deleteItem.id, deleteItem.title);
      closeDeleteConfirm();
      close();
    });
  };

  const toggleActive = async (item) => {
    try {
      await ApiArticles.toggleStatus(item.id);
      openNotification?.({
        type: "success",
        message: <p>Status changed</p>,
      });
      await getResources();
      await refreshActive(item.id);
    } catch (err) {
      if (isReferencedError(err)) {
        openNotification?.({
          type: "error",
          message: (
            <p>Unable to delete this object because it is referenced by another object in the system.</p>
          ),
        });
      } else {
        openNotification?.({
          type: "error",
          message: <p>Failed to change status</p>,
        });
      }
    }
  };

  const confirmToggle = async () => {
    if (!toggleItem) return;

    await runLockedAction("toggle", async () => {
      await toggleActive(toggleItem);
      closeToggleConfirm();
    });
  };

  useEffect(() => {
    if (!id) {
      openedIdRef.current = null;
      return;
    }

    if (openedIdRef.current === String(id)) return;
    openedIdRef.current = String(id);

    const openById = async () => {
      const fromList = list.find((item) => String(item.id) === String(id));

      if (fromList?.text) {
        open(fromList, mode);
        return;
      }

      try {
        const resp = await ApiArticles.getById(id);
        const full = resp?.data?.page || resp?.data?.article || resp?.data;
        if (full) open(full, mode);
      } catch (e) {
        console.error("Failed to fetch article by id:", e);
      }
    };

    openById();
  }, [id, mode, open, list]);

  useEffect(() => {
    if (mode === "create") createAndOpen();
  }, [mode, createAndOpen]);

  useEffect(() => {
    if (!id && active && mode !== "create") close();
  }, [location.pathname, id, active, mode, close]);

  const handlePublish = async (id) => {
    await runLockedAction("publish", async () => {
      try {
        await ApiArticles.publish(id);
        openNotification?.({
          type: "success",
          message: <p>Article has been published</p>,
        });
        // No list refetch here: the modal calls onClose() right after publish,
        // and close() already reloads the list once. refreshActive keeps the
        // (briefly still-open) article view in sync.
        await refreshActive(id);
      } catch (error) {
        console.error("Publish error:", error);
        openNotification?.({
          type: "error",
          message: <p>Failed to publish Article</p>,
        });
      }
    });
  };

  const handleDiscard = async (id) => {
    if (!id) return;

    await runLockedAction("discard", async () => {
      try {
        await ApiArticles.discard(id);
        openNotification?.({
          type: "success",
          message: <p>Changes discarded</p>,
        });
        await getResources();
        await refreshActive(id);
      } catch (error) {
        console.error("Discard error:", error);
        openNotification?.({
          type: "error",
          message: <p>Failed to discard changes</p>,
        });
      }
    });
  };


  const handleDuplicate = async (item) => {
    if (!item?.id) return;

    await runLockedAction("duplicate", async () => {
      const resp = await ApiArticles.duplicate(item.id);
      const newPage = resp?.data?.page || resp?.data;

      openNotification?.({
        type: "success",
        message: <p>Article has been duplicated</p>,
      });

      await getResources();

      if (newPage?.id) {
        open(newPage, "edit");
        navigate(`/content/articles/${newPage.id}/edit`);
      }
    });
  };

  const statuses = [
    { id: "active", name: "Active" },
    { id: "pending", name: "Pending" },
    { id: "inactive", name: "Inactive" },
  ];

  return (
    <Body
      header={{
        title: "Articles",
        create: canCreate && { label: "Create article", onClick: createAndOpen, testID: "article-create" },
        search: { value: search, onHandleChange: handleUpdateSearchValue },
      }}
      filters={
        <DropdownFilters
          mode="controlled"
          filter={filters}
          filtersBy={[
            { name: "Tags", id: "tag", children: tags },
            { name: "Category", id: "categoryIds", children: categories },
            { name: "Author", id: "userIds", children: authors },
            { name: "Status", id: "statuses", children: statuses },
            {
              name: "Source",
              id: "external",
              children: [
                { id: true, name: "External" },
                { id: false, name: "Internal" },
              ],
            },
          ]}
          updateFilterValue={updateFilterValueControlled}
        />
      }
    >
      <ArticleModal
        articleId={active?.id}
        form={form}
        data={active}
        onClose={close}
        onSave={update}
        tags={tags}
        categories={categories}
        updateHasImage={updateHasImage}
        updateIsUpdatedImage={updateIsUpdatedImage}
        isLoading={isLoading || isActionLoading}
        authors={authors}
        mode={mode}
        onDiscard={handleDiscard}
        onPublish={handlePublish}
        onSaveSilent={updateResourceSilent}
        remove={(id, title) => openDeleteConfirm({ id, title })}
        toggleActive={(item) => openToggleConfirm(item)}
        duplicate={handleDuplicate}
      />

      <AdminResourceTable
        data={list}
        onHandleRowClick={open}
        tableColumnKeys={[
          "cover",
          "title",
          "categories",
          "statusSummary",
          "updatedAt",
        ]}
        isLoading={isLoading}
        shouldDropCache={isUpdatedImage}
        updateSorting={updateSorting}
        hasMore={hasMore}
        fetchData={fetchNext}
        selectionColumn={getSelectionColumn()}
        containerRef={tableRef}
        after={
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDelete}
            loading={isBulkDeleting}
          />
        }
      />

      <DeleteModal
        title={deleteItem?.title}
        question={"Are you sure you want to delete this article?"}
        handleOk={confirmDelete}
        handleCancel={closeDeleteConfirm}
        isModalOpen={isDeleteOpen}
      />

      <DeleteModal
        title={toggleItem?.title}
        question={`Are you sure you want to ${
          toggleItem?.isActive ? "deactivate" : "activate"
        } this article?`}
        handleOk={confirmToggle}
        handleCancel={closeToggleConfirm}
        isModalOpen={isToggleOpen}
      />
    </Body>
  );
};